import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { PageHeader, StatCard, Panel, Pill } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import {
  HeartHalf, Heart, Baby, ForkKnife, Calendar, Sparkle, ChartLineUp,
} from "@phosphor-icons/react";
import { toast } from "sonner";

export default function MedicalCoordDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    students: 0,
    vaccinations_due: 0,
    health_incidents: 0,
    allergic_students: 0,
  });
  const [dueList, setDueList] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // /vaccinations doesn't have a list endpoint — fetch students and
        // derive an allergic-students count for now. Vaccinations summary
        // comes from /vaccinations/branch-summary (graceful 404 → null).
        const [students, vaccSummary] = await Promise.all([
          api.get("/students"),
          api.get("/vaccinations/branch-summary").catch(() => ({ data: null })),
        ]);
        if (cancelled) return;
        const studentsList = students.data || [];
        const allergic = studentsList.filter(
          (s) => s.allergies && s.allergies.toLowerCase() !== "none" && s.allergies !== "",
        );
        const dueCount = vaccSummary?.data?.due_count || 0;
        setStats({
          students: studentsList.length,
          vaccinations_due: dueCount,
          health_incidents: 0,
          allergic_students: allergic.length,
        });
        setDueList(vaccSummary?.data?.due || []);
      } catch (e) {
        // Silent — page should still render its KPIs.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div data-testid="medical-coord-dashboard">
      <PageHeader
        eyebrow="Medical Coordinator"
        title={`Hello, ${user?.name?.split(" ")[0] || "Doc"} 🩺`}
        subtitle="Track health records, vaccinations, allergies and incidents across your branch."
        action={
          <Link to="/app/vaccinations">
            <Button className="bg-terracotta hover:bg-terracotta/90 text-white">
              <HeartHalf size={16} /> Manage vaccinations
            </Button>
          </Link>
        }
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard label="Students" value={stats.students} icon={Baby} accent="terracotta" />
        <StatCard label="Vaccinations due" value={stats.vaccinations_due} icon={HeartHalf} accent="mustard" hint={<Link to="/app/vaccinations" className="underline text-xs">View</Link>} />
        <StatCard label="Allergic students" value={stats.allergic_students} icon={Heart} accent="sage" />
        <StatCard label="Health incidents (90d)" value={stats.health_incidents} icon={Sparkle} accent="lavender" />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Panel title="Vaccinations due">
          {loading ? (
            <div className="py-12 text-center text-ink-soft text-sm">Loading…</div>
          ) : dueList.length === 0 ? (
            <div className="py-12 text-center text-ink-soft text-sm">
              All caught up! No vaccinations are overdue. ✨
            </div>
          ) : (
            <div className="space-y-2">
              {dueList.map((v, i) => (
                <div key={v.vaccination_id || i} className="flex items-center justify-between p-2.5 rounded-lg bg-paper border border-border">
                  <div>
                    <div className="text-sm font-medium">{v.student_name || v.student_id} · {v.vaccine_name}</div>
                    <div className="text-xs text-ink-soft">Due: {v.due_date}</div>
                  </div>
                  <Pill tone="warning">due</Pill>
                </div>
              ))}
            </div>
          )}
        </Panel>

        <Panel title="Quick actions">
          <div className="space-y-2">
            <Link to="/app/vaccinations" className="block p-3 rounded-xl bg-terracotta-soft hover:bg-terracotta-soft/80 transition">
              <div className="flex items-center gap-2 text-terracotta font-semibold text-sm"><HeartHalf size={16} /> Vaccination tracker</div>
              <div className="text-xs text-ink-soft mt-1">Update due dates + upload certificates.</div>
            </Link>
            <Link to="/app/health" className="block p-3 rounded-xl bg-sage-soft hover:bg-sage-soft/80 transition">
              <div className="flex items-center gap-2 text-sage-deep font-semibold text-sm"><Heart size={16} /> Health records</div>
              <div className="text-xs text-ink-soft mt-1">View student-by-student medical history.</div>
            </Link>
            <Link to="/app/students" className="block p-3 rounded-xl bg-mustard-soft hover:bg-mustard-soft/80 transition">
              <div className="flex items-center gap-2 text-mustard-hover font-semibold text-sm"><Baby size={16} /> Students</div>
              <div className="text-xs text-ink-soft mt-1">Check allergies, blood groups, emergency contacts.</div>
            </Link>
            <Link to="/app/meals" className="block p-3 rounded-xl bg-lavender-soft hover:bg-lavender-soft/80 transition">
              <div className="flex items-center gap-2 text-lavender-deep font-semibold text-sm"><ForkKnife size={16} /> Allergen-aware meals</div>
              <div className="text-xs text-ink-soft mt-1">Review today's menu against allergens.</div>
            </Link>
          </div>
        </Panel>
      </div>
    </div>
  );
}
